// Procedural sound-effects + master mute. 100% ORIGINAL: every SFX is synthesized live with the
// Web Audio API (oscillator + gain envelopes) — no audio asset files, so nothing to license and
// nothing to ship. Mute persists to localStorage (mirrors the i18n locale pattern). The
// AudioContext is created LAZILY on the first play() (which always runs inside a user-gesture
// handler, e.g. a button pointerup), so the browser autoplay policy never blocks it. Off-DOM
// (tests / headless / no Web Audio) every call safely no-ops.

export type SfxName = 'uiTap' | 'uiBack' | 'success' | 'error' | 'reveal'

const STORAGE_KEY = 'jkgame.muted'

interface Tone {
  freq: number
  dur: number
  type?: OscillatorType
  gain?: number
  slideTo?: number // linear pitch glide to this freq over `dur` (omit = steady)
  delay?: number // seconds after trigger before this tone starts (for short melodies)
}

// Original SFX presets — short, subtle, gameplay-neutral cues.
const SFX: Record<SfxName, Tone[]> = {
  uiTap: [{ freq: 660, dur: 0.05, type: 'triangle', gain: 0.16 }],
  uiBack: [{ freq: 380, dur: 0.07, type: 'triangle', gain: 0.16 }],
  success: [
    { freq: 660, dur: 0.09, type: 'sine', gain: 0.2 },
    { freq: 990, dur: 0.13, type: 'sine', gain: 0.2, delay: 0.08 },
  ],
  error: [{ freq: 150, dur: 0.18, type: 'square', gain: 0.14, slideTo: 110 }],
  reveal: [
    { freq: 523, dur: 0.1, type: 'sine', gain: 0.18 },
    { freq: 784, dur: 0.16, type: 'sine', gain: 0.2, delay: 0.09 },
  ],
}

let muted = readMuted()
let ctx: AudioContext | null = null

function readMuted(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false // localStorage unavailable
  }
}

/** Lazily create (and resume) the shared AudioContext. Null off-DOM or where Web Audio is absent. */
function ensureCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!AC) return null
  if (!ctx) ctx = new AC()
  if (ctx.state === 'suspended') void ctx.resume() // unlock after the first gesture
  return ctx
}

function playTone(ac: AudioContext, t: Tone): void {
  const osc = ac.createOscillator()
  const g = ac.createGain()
  osc.type = t.type ?? 'sine'
  const start = ac.currentTime + (t.delay ?? 0)
  osc.frequency.setValueAtTime(t.freq, start)
  if (t.slideTo) osc.frequency.linearRampToValueAtTime(t.slideTo, start + t.dur)
  const peak = t.gain ?? 0.2
  // Attack/decay envelope (exponential ramps avoid the click of an instant on/off).
  g.gain.setValueAtTime(0.0001, start)
  g.gain.exponentialRampToValueAtTime(peak, start + 0.008)
  g.gain.exponentialRampToValueAtTime(0.0001, start + t.dur)
  osc.connect(g)
  g.connect(ac.destination)
  osc.start(start)
  osc.stop(start + t.dur + 0.02)
}

export const audio = {
  /** Play a named procedural SFX. No-op while muted, off-DOM, or if Web Audio is unavailable. */
  play(name: SfxName): void {
    if (muted) return
    const ac = ensureCtx()
    if (!ac) return
    try {
      for (const tone of SFX[name]) playTone(ac, tone)
    } catch {
      /* audio hardware/context error — never let SFX break gameplay */
    }
  },
  isMuted(): boolean {
    return muted
  },
  setMuted(m: boolean): void {
    muted = m
    try {
      localStorage.setItem(STORAGE_KEY, m ? '1' : '0')
    } catch {
      /* ignore */
    }
  },
  /** Flip mute and return the new state (for a settings toggle). */
  toggleMuted(): boolean {
    this.setMuted(!muted)
    return muted
  },
}
